/**
 * Webhook Config Table
 *
 * Data-heavy table layout for webhook configurations.
 * Per Design 5.1: Table (not cards), with actions menu per row.
 */

import { MoreHorizontal, ExternalLink, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui';
import { formatDate, truncate } from '@/lib/utils';
import { MerchantCell } from '@/components/shared/merchant-cell';
import type { WebhookConfig } from '@/types/webhook-config';

interface WebhookConfigTableProps {
  configs: WebhookConfig[];
  onView: (merchantId: string) => void;
  onEdit?: (config: WebhookConfig) => void;
  onTest?: (merchantId: string) => void;
  onRotateSecret?: (merchantId: string) => void;
  onDelete?: (merchantId: string) => void;
  /** Spec 0220: Merchant-Spalte nur fuer Platform-Rollen (Default: sichtbar). */
  showMerchant?: boolean;
}

export function WebhookConfigTable({
  configs,
  onView,
  onEdit,
  onTest,
  onRotateSecret,
  onDelete,
  showMerchant = true,
}: WebhookConfigTableProps) {
  const { t } = useTranslation('webhook-config');

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          {showMerchant && <TableHead>{t('table.merchant')}</TableHead>}
          <TableHead>{t('table.url')}</TableHead>
          <TableHead>{t('table.enabled')}</TableHead>
          <TableHead>{t('table.updatedAt')}</TableHead>
          <TableHead>{t('table.updatedBy')}</TableHead>
          <TableHead className="w-10">
            <span className="sr-only">{t('table.actions')}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {configs.length === 0 ? (
          <TableRow>
            <TableCell colSpan={showMerchant ? 6 : 5} className="text-center text-muted-foreground py-8">
              {t('table.empty', { defaultValue: 'No webhook configurations found.' })}
            </TableCell>
          </TableRow>
        ) : (
          configs.map((config) => (
            <TableRow key={config.merchantId}>
              {showMerchant && (
                <TableCell>
                  <MerchantCell merchantId={config.merchantId} />
                </TableCell>
              )}
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm truncate max-w-[280px]" title={config.webhookUrl}>
                    {truncate(config.webhookUrl, 40)}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 coarse:min-h-11 coarse:min-w-11"
                    onClick={() => copyToClipboard(config.webhookUrl)}
                    title={t('table.copyUrl')}
                    aria-label={t('table.copyUrl')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </TableCell>
              <TableCell>
                <Badge variant={config.enabled ? 'default' : 'secondary'}>
                  {config.enabled ? t('status.active') : t('status.inactive')}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(config.updatedAt)}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs">
                  {config.updatedBy}
                </Badge>
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 coarse:min-h-11 coarse:min-w-11">
                      <MoreHorizontal className="h-4 w-4" />
                      <span className="sr-only">{t('table.actions')}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onView(config.merchantId)}>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t('actions.view', { defaultValue: 'View Details' })}
                    </DropdownMenuItem>
                    {onEdit && (
                      <DropdownMenuItem onClick={() => onEdit(config)}>
                        {t('actions.edit', { defaultValue: 'Edit URL' })}
                      </DropdownMenuItem>
                    )}
                    {onTest && (
                      <DropdownMenuItem onClick={() => onTest(config.merchantId)}>
                        {t('test.button')}
                      </DropdownMenuItem>
                    )}
                    {onRotateSecret && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onRotateSecret(config.merchantId)}>
                          {t('actions.rotateSecret', { defaultValue: 'Rotate Secret' })}
                        </DropdownMenuItem>
                      </>
                    )}
                    {onDelete && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onDelete(config.merchantId)}
                          className="text-destructive"
                        >
                          {t('delete.confirm')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
